import React, { useRef } from "react";
import { Printer } from "lucide-react";

// ─── Safe Indian-rupee formatter ─────────────────────────────────────────────
const fmt = (n) => {
  const num = Number(n);
  return isFinite(num) ? num.toLocaleString("en-IN") : "0";
};

/**
 * QuotationPreview
 *
 * Self-contained pricing using the FULL tiered discount matrix.
 * Each cartItem must carry:
 *   item.priceExclGST   — Amazon price excl. GST (our quote base)
 *   item.amazonPrice    — Amazon price incl. GST (classification: weight ≤2kg, price <₹50K)
 *
 * DISCOUNT MATRIX (% off priceExclGST):
 *  Qty 1–2     Light Low→2.1%  Light High→1.5%  Heavy→1.0%
 *  Qty 3–5     Light Low→4.1%  Light High→3.5%  Heavy→2.5%
 *  Qty 6–9     Light Low→5.4%  Light High→5.2%  Heavy→3.8%
 *  Qty 10–49   Light Low→9.5%  Light High→9.0%  Heavy→5.5%
 *  Qty 50–100  Light Low→10%   Light High→9.0%  Heavy→6.0%
 *  Qty 101–500 Light Low→11%   Light High→9.0%  Heavy→7.0%
 *  Qty 500+    Light→9.0%                        Heavy→6.0%
 * EXPRESS: +₹450/kg/unit
 */
const QuotationPreview = ({ quoteData }) => {
  const printRef = useRef();

  if (!quoteData) return null;

  const {
    orgName,
    contactPerson,
    email,
    phone,
    address,
    city,
    state,
    pincode,
    deliveryOption,
    notes,
    cartItems = [],
    quoteNumber,
    date,
  } = quoteData;

  // ─── Self-contained pricing (matches pricingEngine.js exactly) ──────────────
  const buildLineItems = () => {
    let subtotal = 0;

    const items = cartItems.map((item) => {
      const amazonPriceWithGST = Number(item.amazonPrice ?? item.price ?? 0);
      // Use explicit priceExclGST if available, else derive from Amazon price
      const basePriceExclGST  = Number(
        item.priceExclGST ?? Math.round(amazonPriceWithGST / 1.18)
      );
      const weight       = Number(item.weight)   || 1.0;
      const quantity     = Number(item.quantity) || 1;
      const deliveryType = item.deliveryType || deliveryOption || "normal";

      // ── Classification ──────────────────────────────────────────────────
      const isLight    = weight <= 2;
      const isLowPrice = amazonPriceWithGST < 50000;

      // ── Discount lookup ─────────────────────────────────────────────────
      let discountPct = 0;
      if (quantity >= 500) {
        discountPct = isLight ? 9.0 : 6.0;
      } else if (quantity >= 101) {
        discountPct = isLight ? (isLowPrice ? 11.0 : 9.0) : 7.0;
      } else if (quantity >= 50) {
        discountPct = isLight ? (isLowPrice ? 10.0 : 9.0) : 6.0;
      } else if (quantity >= 10) {
        discountPct = isLight ? (isLowPrice ? 9.5  : 9.0) : 5.5;
      } else if (quantity >= 6) {
        discountPct = isLight ? (isLowPrice ? 5.4  : 5.2) : 3.8;
      } else if (quantity >= 3) {
        discountPct = isLight ? (isLowPrice ? 4.1  : 3.5) : 2.5;
      } else {
        discountPct = isLight ? (isLowPrice ? 2.1  : 1.5) : 1.0;
      }

      // ── Price calculation ───────────────────────────────────────────────
      const discountedUnit         = basePriceExclGST * (1 - discountPct / 100);
      const expressShippingPerUnit = deliveryType === "express" ? weight * 450 : 0;
      const perUnit                = Math.round(discountedUnit + expressShippingPerUnit);
      const finalPrice             = perUnit * quantity;
      const expressShippingCost    = Math.round(expressShippingPerUnit * quantity);
      const originalTotal          = Math.round(basePriceExclGST * quantity);
      const savings                = Math.max(0, originalTotal - finalPrice);
      const savingsPct             = originalTotal > 0
        ? Math.round((savings / originalTotal) * 100)
        : 0;

      subtotal += finalPrice;

      return {
        ...item,
        basePriceExclGST,
        amazonPriceWithGST,
        weight,
        quantity,
        deliveryType,
        perUnit,
        finalPrice,
        discountPct,
        expressShippingCost,
        savings,
        savingsPct,
        isLight,
        isLowPrice,
      };
    });

    const gstAmount  = Math.round(subtotal * 0.18);
    const grandTotal = subtotal + gstAmount;
    return { items, subtotal, gstAmount, grandTotal };
  };

  const { items, subtotal, gstAmount, grandTotal } = buildLineItems();

  // ─── Print handler ────────────────────────────────────────────────────────
  const handlePrint = () => {
    const content = printRef.current.innerHTML;
    const win = window.open("", "_blank");
    win.document.write(`<!DOCTYPE html><html><head>
      <title>Quotation ${quoteNumber}</title>
      <style>
        *{margin:0;padding:0;box-sizing:border-box}
        body{font-family:Arial,sans-serif;font-size:12px;color:#1a1a1a}
        table{width:100%;border-collapse:collapse}
        th,td{border:1px solid #d1d5db;padding:8px 10px;text-align:left}
        th{background:#1d4ed8;color:#fff;font-weight:700}
        tfoot td{font-weight:600}
        @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}.no-break{page-break-inside:avoid}}
      </style>
    </head><body>${content}</body></html>`);
    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

  return (
    <section id="quotation-preview" className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-4xl mx-auto">

        {/* Action bar */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Quotation Preview</h2>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-2.5 rounded-xl transition text-sm shadow-sm"
          >
            <Printer className="h-4 w-4" />
            Print / Save PDF
          </button>
        </div>

        {/* Document */}
        <div ref={printRef} className="bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden no-break">

          {/* ── Light sky blue header ── */}
          <div className="bg-[#b9e2f5] px-8 py-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xl font-bold tracking-tight mb-1 text-[#0f3b5c]">
                  Orator Hype Private Limited
                </div>
                <div className="text-[#2c6280] text-xs leading-relaxed mt-1">
                  Office No.3, Ground Floor, Kanji Moraji Bldg, 9,<br />
                  Keshavji Naik Rd, Mumbai, Maharashtra 400009<br />
                  account@oratorhype.com &nbsp;|&nbsp; +91 73046 60050<br />
                  GSTIN: 27AAECO8036H1ZA
                </div>
              </div>
              <div className="text-right flex-shrink-0 ml-6">
                <div className="text-[#1f6390] text-xs uppercase tracking-wider mb-1">Proposal Number</div>
                <div className="font-bold text-lg text-[#0f3b5c]">{quoteNumber}</div>
                <div className="text-[#1f6390] text-xs uppercase tracking-wider mt-3 mb-1">Date</div>
                <div className="font-medium text-sm text-[#1f3a4b]">{date}</div>
              </div>
            </div>
          </div>

          <div className="px-8 py-6">

            {/* ── Greeting ── */}
            <div className="mb-6 pb-6 border-b border-gray-200">
              <p className="text-sm text-gray-800">
                Dear <strong>{contactPerson || "Client"}</strong>,
              </p>
              <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                Greetings from OHPL. Please find attached the quotation for your review.
              </p>
            </div>

            {/* ── Bill To / Delivery ── */}
            <div className="grid grid-cols-2 gap-8 mb-8 pb-6 border-b border-gray-200">
              <div>
                <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Bill To</div>
                <div className="font-bold text-gray-900 text-base">{orgName || "Customer"}</div>
                <div className="text-sm text-gray-600 mt-1 leading-relaxed">
                  {contactPerson && <div>Attn: {contactPerson}</div>}
                  {phone         && <div>{phone}</div>}
                  {email         && <div className="text-blue-600">{email}</div>}
                  {address && (
                    <div className="mt-1">
                      <div>{address}</div>
                      {(city || state || pincode) && (
                        <div>{[city, state, pincode].filter(Boolean).join(", ")}</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Delivery Details</div>
                <span className={`inline-block px-3 py-1 rounded-lg text-xs font-semibold ${
                  deliveryOption === "express"
                    ? "bg-orange-100 text-orange-700"
                    : "bg-green-100 text-green-700"
                }`}>
                  {deliveryOption === "express" ? " Express — 48 Hours" : " Standard — 7–10 Days"}
                </span>
                {notes && (
                  <p className="mt-2 text-gray-500 italic text-xs leading-relaxed bg-gray-50 p-2 rounded">
                     {notes}
                  </p>
                )}
              </div>
            </div>

            {/* ── Order Summary table (Excl. GST) ── */}
            <div className="mb-6">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Order Summary</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-blue-700 text-white">
                      <th className="border border-blue-600 px-3 py-3 text-center w-12 font-semibold">Sr. No.</th>
                      <th className="border border-blue-600 px-3 py-3 text-left font-semibold">Product Description</th>
                      <th className="border border-blue-600 px-3 py-3 text-center w-16 font-semibold">Qty</th>
                      <th className="border border-blue-600 px-3 py-3 text-right w-36 font-semibold">
                        Unit Price<br />
                        <span className="text-xs font-normal text-blue-200">(Excl. GST)</span>
                      </th>
                      <th className="border border-blue-600 px-3 py-3 text-right w-36 font-semibold">
                        Sub Total<br />
                        <span className="text-xs font-normal text-blue-200">(Excl. GST)</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length > 0 ? items.map((item, idx) => (
                      <tr key={item.id || idx} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                        <td className="border border-gray-300 px-3 py-3 text-gray-500 text-center">{idx + 1}</td>
                        <td className="border border-gray-300 px-3 py-3">
                          <div className="font-semibold text-gray-900">{item.name || "Product"}</div>
                          <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                            {item.brand && item.brand !== "Unknown Brand" && (
                              <div>Brand: {item.brand}</div>
                            )}
                            {item.model && <div>Model: {item.model}</div>}
                            <div className="text-gray-400">
                              {item.isLight ? "Light (≤2kg)" : "Heavy (>2kg)"} ·{" "}
                              {item.isLowPrice ? "Under ₹50K" : "Above ₹50K"} ·{" "}
                              {item.discountPct}% off
                              {item.deliveryType === "express"
                                ? ` + ₹${fmt(item.expressShippingCost)} express`
                                : ""}
                            </div>
                          </div>
                        </td>
                        <td className="border border-gray-300 px-3 py-3 text-center font-bold text-gray-900">
                          {item.quantity}
                        </td>
                        <td className="border border-gray-300 px-3 py-3 text-right font-semibold text-gray-800">
                          ₹{fmt(item.perUnit)}
                        </td>
                        <td className="border border-gray-300 px-3 py-3 text-right font-bold text-blue-700 text-base">
                          ₹{fmt(item.finalPrice)}
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="5" className="border border-gray-300 px-4 py-8 text-center text-gray-500">
                          No items in quotation
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50">
                      <td colSpan="4" className="border border-gray-300 px-4 py-3 text-right font-semibold text-gray-700">
                        Subtotal (Excl. GST)
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-right font-semibold text-gray-900">
                        ₹{fmt(subtotal)}
                      </td>
                    </tr>
                    <tr className="bg-yellow-50">
                      <td colSpan="4" className="border border-gray-300 px-4 py-3 text-right font-semibold text-gray-700">
                        GST @ 18%
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-right font-semibold text-yellow-700">
                        ₹{fmt(gstAmount)}
                      </td>
                    </tr>
                    <tr className="bg-blue-50">
                      <td colSpan="4" className="border border-gray-300 px-4 py-4 text-right font-bold text-gray-900 text-base">
                        Grand Total (Incl. GST)
                      </td>
                      <td className="border border-gray-300 px-4 py-4 text-right font-bold text-blue-700 text-xl">
                        ₹{fmt(grandTotal)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* ── Order summary strip ── */}
            {items.length > 0 && (
              <div className="p-4 bg-gray-50 rounded-lg space-y-2 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total Units Ordered:</span>
                  <span className="font-bold text-gray-900">
                    {items.reduce((s, i) => s + i.quantity, 0)} units
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total Weight:</span>
                  <span className="font-bold text-gray-900">
                    {items.reduce((s, i) => s + (Number(i.weight) || 1.0) * i.quantity, 0).toFixed(1)} kg
                  </span>
                </div>
              </div>
            )}

            {/* ── Terms & Conditions ── */}
            <div className="border border-gray-200 rounded-lg overflow-hidden mb-6">
              <div className="bg-gray-100 px-4 py-2">
                <p className="text-xs font-bold text-gray-700 uppercase tracking-wider"> Terms &amp; Conditions</p>
              </div>
              <div className="p-4 text-xs text-gray-700 space-y-2">
                <div className="grid grid-cols-[160px_1fr] gap-x-3 gap-y-2">
                  <span className="text-gray-500 font-medium">Delivery Scope:</span>
                  <span>Supply only. No onsite service or installation is included.</span>
                  <span className="text-gray-500 font-medium">Installation Charges:</span>
                  <span>Wiring, digging, and related work will be charged separately on an actual basis.</span>
                  <span className="text-gray-500 font-medium">Quotation Validity:</span>
                  <span>Valid for <strong>3 days</strong> for the specified quantity.</span>
                  <span className="text-gray-500 font-medium">Payment Terms:</span>
                  <span>100% advance payment required for procurement of materials.</span>
                  <span className="text-gray-500 font-medium">Warranty:</span>
                  <span>Provided by the respective brand/manufacturer, ranging from 1 to 5 years.</span>
                  <span className="text-gray-500 font-medium">GST:</span>
                  <span>Applicable GST @ 18% will be charged extra as per billing requirements.</span>
                  <span className="text-gray-500 font-medium">Delivery Timeline:</span>
                  <span>
                    Express: Within 48 hours (within 30 km of a major airport)<br />
                    Standard: 7–10 working days<br />
                    <em className="text-gray-400">From date of PO confirmation &amp; receipt of full payment (dispatch from Mumbai).</em>
                  </span>
                  <span className="text-gray-500 font-medium">Credit Policy:</span>
                  <span>Goods are not supplied on a credit basis.</span>
                </div>

                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="font-bold text-gray-700 mb-1">Important Notes</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-600">
                    <li>The order will be processed only upon receipt of an official Purchase Order (PO).</li>
                    <li>Pricing and availability are subject to confirmation at the time of order.</li>
                  </ul>
                </div>

                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="font-bold text-gray-700 mb-1">Additional Terms</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-600">
                    <li>
                      Send the PO via email to: <strong>Sales@oratorhype.com</strong>{" "}
                      (CC: kk@oratorhype.com, aryan@oratorhype.com, account@oratorhype.com) with complete delivery details — contact person, phone, full address, and PIN code.
                    </li>
                    <li>Once payment is received and order dispatched, a tracking ID will be shared.</li>
                    <li>For queries, call or WhatsApp: <strong>+91 73046 60050</strong></li>
                  </ul>
                </div>
              </div>
            </div>

          </div>

          {/* ── Light sky blue footer ── */}
          <div className="bg-[#b9e2f5] text-center px-8 py-5">
            <p className="text-xs text-[#1f6390]">
              Orator Hype Private Limited &nbsp;|&nbsp; sales@oratorhype.com &nbsp;|&nbsp; +91 73046 60050 &nbsp;|&nbsp; www.oratorhype.com
            </p>
            <p className="text-xs text-[#166a8f] italic mt-1">
              Growing Indian startup delivering scalable SaaS, IT, Cybersecurity, Healthcare, Technology &amp; Physical Marketing solutions.
            </p>
          </div>

        </div>
      </div>
    </section>
  );
};

export default QuotationPreview;